import { careModelAnalytics } from 'src/services/analytics';
import { network as useNetworkStore } from 'src/store/network';
import { computed } from 'vue';

import { createCheckoutAudit } from '../../application/useCases/createCheckoutAudit';
import { createCheckoutAuditor } from '../../application/useCases/createCheckoutAuditor';
import { auditSessionActivity } from '../../infrastructure/api/auditApi';
import { useAuditRunClock } from './useAuditRunClock';
import { useAuditRunState } from './useAuditRunState';
import { useAuditorAccess } from './useAuditorAccess';

/**
 * Wires the use case to this app's stores, API and analytics. Resolved once per caller rather than
 * inside each getter, so a re-render costs no store lookups.
 */
function auditorFor({ getSessionActivity, getSessionNoteTemplate, getNotes, getIsSuspended }) {
  const networkStore = useNetworkStore();
  const { canRunCheckoutAudit } = useAuditorAccess();
  const now = useAuditRunClock();

  return createCheckoutAuditor({
    getSessionActivity,
    getSessionNoteTemplate,
    getNotes,
    getIsSuspended,
    getIsOffline: () => !networkStore.networkOnline || networkStore.offlineMode,
    getNow: () => now.value,
    getCanRunAudit: () => canRunCheckoutAudit.value,
    runState: useAuditRunState(),
    audit: createCheckoutAudit(auditSessionActivity),
    analytics: {
      trackRun: (event) => careModelAnalytics.trackCheckoutAuditRun(event),
      trackNoteSigned: (event) => careModelAnalytics.trackCheckoutAuditNoteSigned(event),
    },
  });
} // auditorFor()

/**
 * Drives the auditor on the summary step, where the note is still in the editor: "outdated" means
 * the text differs from what was reviewed, not that a saved-note timestamp moved.
 *
 * @param {Object} params
 * @param {() => boolean} [params.getIsSuspended] - Contexts that must never audit, e.g. a template preview.
 */
export function useCheckoutAuditor(params) {
  const auditor = auditorFor(params);

  return {
    status: computed(auditor.status),
    rationale: computed(auditor.rationale),
    isEnabled: computed(auditor.isEnabled),
    isRunning: computed(auditor.isRunning),
    canRerun: computed(auditor.canRerun),
    start: auditor.start,
    rerun: auditor.rerun,
  };
}

/**
 * For the wizard, which owns signing but does not render the card.
 *
 * @param {Object} params - The same getters useCheckoutAuditor takes.
 */
export function reportCheckoutAuditNoteSigned(params) {
  auditorFor(params).reportNoteSigned();
} // reportCheckoutAuditNoteSigned()
